import { mapKeys } from 'lodash';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import type {
  SelectionCategories,
  SelectionList,
  TreeItem,
  ValidValueTypes,
} from '@revelio/iso-utility';

import type { TreeApi } from '../useTreeApi';
import { BreadcrumbTree, type BreadcrumbTreeProps } from './BreadcrumbTree';
import {
  getNestedId,
  nestSelectionLists,
  type SelectionListItems,
  validateSelectionLists,
} from './breadcrumb-tree-utils';

// Prefer list by depth, but fall back to any list that contains the item id
const resolveSelectionList = (
  lists: SelectionListItems[],
  depth: number,
  lastId: string
): SelectionListItems | undefined => {
  const byDepth = lists[depth];
  if (byDepth && byDepth.value.some((i) => String(i.id) === String(lastId))) {
    return byDepth;
  }
  return lists.find((l) =>
    l.value.some((i) => String(i.id) === String(lastId))
  );
};

export type BreadcrumbTreeSelectionProps = Pick<
  BreadcrumbTreeProps,
  'branches' | 'height'
> & {
  initialSelections: Record<string, TreeItem>;
  selectionLists: SelectionList<ValidValueTypes>[];
  maxSelections?: number;
  onMaxExceeded?: () => void;
  onChange?: (items: Record<string, TreeItem>) => void;
  isSelectionListIdsEnabled?: boolean;
};

export const BreadcrumbTreeSelection = forwardRef<
  TreeApi,
  BreadcrumbTreeSelectionProps
>(
  (
    {
      initialSelections = {},
      branches,
      selectionLists,
      maxSelections,
      onMaxExceeded,
      onChange,
      height,
      isSelectionListIdsEnabled,
    },
    ref
  ) => {
    const [selections, setSelections] = useState<string[]>([]);
    const hasUserInteractedRef = useRef<boolean>(false);

    // Filter selection lists to only include requested IDs and validate them
    const validatedSelectionLists = useMemo(() => {
      try {
        const lists = validateSelectionLists(selectionLists);
        return lists;
      } catch {
        return [];
      }
    }, [selectionLists]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
    useEffect(() => {
      /** map initial tree selections to nestedIds
       * then setSelections */
      try {
        const applicableSelections = Object.values(initialSelections).filter(
          (item) =>
            validatedSelectionLists
              .map((list) => list.id)
              .includes(item.selectionListId as SelectionCategories)
        );
        const nestedIds = applicableSelections.map((item) =>
          getNestedId({ selectionLists: validatedSelectionLists, item })
        );
        // Only apply initial selections on first load when we don't have user selections yet
        if (
          selections.length === 0 &&
          nestedIds.length > 0 &&
          !hasUserInteractedRef.current
        ) {
          setSelections(nestedIds);
        }
      } catch {
        // Only clear on first mount; otherwise retain user selections
        if (selections.length === 0) {
          setSelections([]);
        }
      }
    }, [validatedSelectionLists]);

    const handleOnChange = (value: string[]) => {
      /** if we are over the max selections, and max selections is not === 1 (single select)
       * show a warning toast that we are over the limit */

      hasUserInteractedRef.current = true;

      const entityIds = value.map((id) => {
        const ids = id.split('_');
        // to avoid conflicts between different levels of granularity
        const granularity = ids.length;
        const lastSegment = ids.pop() ?? '';
        const rawItemId = lastSegment.includes(':')
          ? lastSegment.split(':').pop()
          : lastSegment;
        return `${granularity}_${rawItemId}`;
      });

      const uniqueEntityIdsLength = new Set(entityIds).size;

      if (
        maxSelections &&
        maxSelections > 1 &&
        uniqueEntityIdsLength > maxSelections
      ) {
        onMaxExceeded?.();
        return;
      }

      // otherwise, set the selections
      setSelections(value);

      onChange?.(
        ((currentSelections: string[]) => {
          const selectedTreeItems: TreeItem[] = currentSelections
            .map((id): TreeItem | null => {
              const splitIds = id.split('_');
              const splitIdsLength = splitIds.length;
              const lastSegment = splitIds[splitIdsLength - 1] ?? '';
              const lastId = lastSegment.includes(':')
                ? (lastSegment.split(':').pop() ?? '')
                : lastSegment;

              if (!lastId) return null;

              const selectionList: SelectionListItems | undefined =
                resolveSelectionList(
                  validatedSelectionLists,
                  splitIdsLength - 1,
                  lastId
                );
              if (!selectionList) {
                return null;
              }

              const selectedItem = selectionList.value.find(
                (item) => String(item.id) === String(lastId)
              );

              if (!selectedItem) return null;

              return {
                id: selectedItem.id,
                item: selectedItem,
                selectionListId: selectionList.id,
                children: [],
              };
            })
            .filter((entity): entity is TreeItem => entity !== null);

          const keyed = mapKeys(
            selectedTreeItems,
            (value) => `${value.selectionListId}.${value.id}`
          );
          return keyed;
        })(value)
      );
    };

    const data = useMemo(() => {
      try {
        const nested = nestSelectionLists(validatedSelectionLists, 'shortName');
        return nested;
      } catch {
        return [];
      }
    }, [validatedSelectionLists]);

    return (
      <BreadcrumbTree
        ref={ref}
        data={data}
        value={selections}
        branches={branches}
        onChange={handleOnChange}
        height={height ?? 320}
        singleSelect={maxSelections === 1}
        isSelectionListIdsEnabled={isSelectionListIdsEnabled}
      />
    );
  }
);
